import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/supabase/client'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { toast } from 'sonner'
import { 
  Users, CheckCircle, Clock, XCircle, Calendar, 
  Plus, Trash2, Power, PowerOff, ExternalLink,
  ChevronDown, ChevronUp
} from 'lucide-react'
import { Helmet } from 'react-helmet-async'

// 어드민 유저 ID
const ADMIN_USER_IDS = ['bd873ddc-9c11-4093-a4e6-a2fb74e2cb71']

// 채널 플랫폼 라벨
const CHANNEL_PLATFORM_LABELS: Record<string, string> = {
  tiktok: '틱톡',
  youtube: '유튜브',
  band: '밴드',
  instagram: '인스타그램',
  other: '기타',
}

// 추천 타입
interface Referral {
  id: number
  referrer_id: string
  referee_id: string
  referral_code_id: number
  channel_platform: string
  channel_link: string
  status: 'pending' | 'approved' | 'rejected'
  first_payment_at: string | null
  approved_at: string | null
  rejected_at: string | null
  rejection_reason: string | null
  is_event_period: boolean
  event_id: number | null
  created_at: string
  // 조인된 데이터
  referrer_nickname?: string
  referee_nickname?: string
}

// 이벤트 타입
interface ReferralEvent {
  id: number
  name: string
  start_at: string
  end_at: string
  is_active: boolean
  created_at: string
}

// 통계 타입
interface Stats {
  total: number
  thisMonth: number
  pending: number
  approved: number
  waitingPayment: number
}

export default function AdminReferralPage() {
  const { user } = useAuth()
  const navigate = useNavigate()

  const [stats, setStats] = useState<Stats>({ total: 0, thisMonth: 0, pending: 0, approved: 0, waitingPayment: 0 })
  const [pendingReferrals, setPendingReferrals] = useState<Referral[]>([])
  const [waitingPaymentReferrals, setWaitingPaymentReferrals] = useState<Referral[]>([])
  const [events, setEvents] = useState<ReferralEvent[]>([])
  const [isLoading, setIsLoading] = useState(true)

  // 이벤트 폼 상태
  const [showEventForm, setShowEventForm] = useState(false)
  const [eventForm, setEventForm] = useState({
    name: '',
    start_at: '',
    end_at: '',
  })

  // 거절 모달 상태
  const [rejectingReferralId, setRejectingReferralId] = useState<number | null>(null)
  const [rejectionReason, setRejectionReason] = useState('')

  // 섹션 접기/펼치기
  const [expandedSections, setExpandedSections] = useState({
    waitingPayment: true,
    pending: true,
    events: true,
  })

  // 어드민 체크
  useEffect(() => {
    if (user && !ADMIN_USER_IDS.includes(user.id)) {
      toast.error('접근 권한이 없습니다.')
      navigate('/')
    }
  }, [user, navigate])

  // 데이터 로드
  useEffect(() => {
    if (!user || !ADMIN_USER_IDS.includes(user.id)) return

    const loadData = async () => {
      setIsLoading(true)
      try {
        // 1. 통계
        const now = new Date()
        const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString()

        // 전체 추천 수
        const { count: totalCount } = await supabase
          .from('referral')
          .select('*', { count: 'exact', head: true })

        // 이번 달 추천 수
        const { count: thisMonthCount } = await supabase
          .from('referral')
          .select('*', { count: 'exact', head: true })
          .gte('created_at', firstDayOfMonth)

        // 승인 대기 수
        const { count: pendingCount } = await supabase
          .from('referral')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'pending')
          .not('first_payment_at', 'is', null)

        // 승인 완료 수
        const { count: approvedCount } = await supabase
          .from('referral')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'approved')

        // 결제 대기 수 (first_payment_at이 NULL인 pending 상태)
        const { count: waitingPaymentCount } = await supabase
          .from('referral')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'pending')
          .is('first_payment_at', null)

        setStats({
          total: totalCount || 0,
          thisMonth: thisMonthCount || 0,
          pending: pendingCount || 0,
          approved: approvedCount || 0,
          waitingPayment: waitingPaymentCount || 0,
        })

        // 2. 승인 대기 목록 (첫 결제가 완료된 것만)
        const { data: pendingData } = await supabase
          .from('referral')
          .select('*')
          .eq('status', 'pending')
          .not('first_payment_at', 'is', null)
          .order('first_payment_at', { ascending: true })

        // 3. 결제 대기 목록 (첫 결제가 아직 안 된 것)
        const { data: waitingPaymentData } = await supabase
          .from('referral')
          .select('*')
          .eq('status', 'pending')
          .is('first_payment_at', null)
          .order('created_at', { ascending: false })

        // 모든 관련 유저 ID 수집
        const allReferrals = [...(pendingData || []), ...(waitingPaymentData || [])]
        
        if (allReferrals.length > 0) {
          const userIds = [...new Set([
            ...allReferrals.map(r => r.referrer_id),
            ...allReferrals.map(r => r.referee_id),
          ])]

          const { data: membersData } = await supabase
            .from('member')
            .select('user_id, nickname')
            .in('user_id', userIds)

          const nicknameMap = new Map(membersData?.map(m => [m.user_id, m.nickname]) || [])

          // 승인 대기 목록 설정
          if (pendingData && pendingData.length > 0) {
            const enrichedPending = pendingData.map(r => ({
              ...r,
              referrer_nickname: nicknameMap.get(r.referrer_id) || '알 수 없음',
              referee_nickname: nicknameMap.get(r.referee_id) || '알 수 없음',
            }))
            setPendingReferrals(enrichedPending)
          } else {
            setPendingReferrals([])
          }

          // 결제 대기 목록 설정
          if (waitingPaymentData && waitingPaymentData.length > 0) {
            const enrichedWaiting = waitingPaymentData.map(r => ({
              ...r,
              referrer_nickname: nicknameMap.get(r.referrer_id) || '알 수 없음',
              referee_nickname: nicknameMap.get(r.referee_id) || '알 수 없음',
            }))
            setWaitingPaymentReferrals(enrichedWaiting)
          } else {
            setWaitingPaymentReferrals([])
          }
        } else {
          setPendingReferrals([])
          setWaitingPaymentReferrals([])
        }

        // 3. 이벤트 목록
        const { data: eventsData } = await supabase
          .from('referral_event')
          .select('*')
          .order('start_at', { ascending: false })

        setEvents(eventsData || [])

      } catch (error) {
        console.error('[AdminReferral] 데이터 로드 오류:', error)
        toast.error('데이터를 불러오는 중 오류가 발생했습니다.')
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [user])

  // 날짜 포맷
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`
  }

  const formatDateTime = (dateStr: string) => {
    const date = new Date(dateStr)
    return `${formatDate(dateStr)} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
  }

  // 승인 처리
  const handleApprove = async (referral: Referral) => {
    try {
      const { data, error } = await supabase.functions.invoke('admin-referral-approve', {
        body: { referralId: referral.id },
      })

      if (error) throw error
      if (!data?.success) {
        throw new Error(data?.message || '승인 처리에 실패했습니다.')
      }

      toast.success('승인 완료!')

      // 목록 갱신
      setPendingReferrals(prev => prev.filter(r => r.id !== referral.id))
      setStats(prev => ({
        ...prev,
        pending: prev.pending - 1,
        approved: prev.approved + 1,
      }))

    } catch (error) {
      console.error('[AdminReferral] 승인 오류:', error)
      toast.error('승인 처리 중 오류가 발생했습니다.')
    }
  }

  // 거절 처리
  const handleReject = async () => {
    if (!rejectingReferralId || !rejectionReason.trim()) {
      toast.error('거절 사유를 입력해주세요.')
      return
    }

    try {
      const { error } = await supabase
        .from('referral')
        .update({
          status: 'rejected',
          rejected_at: new Date().toISOString(),
          rejection_reason: rejectionReason.trim(),
        })
        .eq('id', rejectingReferralId)

      if (error) throw error

      toast.success('거절 완료!')

      // 목록 갱신
      setPendingReferrals(prev => prev.filter(r => r.id !== rejectingReferralId))
      setStats(prev => ({
        ...prev,
        pending: prev.pending - 1,
      }))

      // 모달 닫기
      setRejectingReferralId(null)
      setRejectionReason('')

    } catch (error) {
      console.error('[AdminReferral] 거절 오류:', error)
      toast.error('거절 처리 중 오류가 발생했습니다.')
    }
  }

  // 이벤트 생성
  const handleCreateEvent = async () => {
    if (!eventForm.name.trim() || !eventForm.start_at || !eventForm.end_at) {
      toast.error('모든 필드를 입력해주세요.')
      return
    }

    try {
      const { data, error } = await supabase
        .from('referral_event')
        .insert({
          name: eventForm.name.trim(),
          start_at: new Date(eventForm.start_at).toISOString(),
          end_at: new Date(eventForm.end_at).toISOString(),
          is_active: false,
        })
        .select()
        .single()

      if (error) throw error

      toast.success('이벤트가 생성되었습니다.')
      setEvents(prev => [data, ...prev])
      setEventForm({ name: '', start_at: '', end_at: '' })
      setShowEventForm(false)

    } catch (error) {
      console.error('[AdminReferral] 이벤트 생성 오류:', error)
      toast.error('이벤트 생성 중 오류가 발생했습니다.')
    }
  }

  // 이벤트 활성화/비활성화 토글
  const handleToggleEvent = async (event: ReferralEvent) => {
    try {
      // 활성화하려는 경우, 다른 활성 이벤트 비활성화
      if (!event.is_active) {
        await supabase
          .from('referral_event')
          .update({ is_active: false })
          .neq('id', event.id)
      }

      const { error } = await supabase
        .from('referral_event')
        .update({ is_active: !event.is_active })
        .eq('id', event.id)

      if (error) throw error

      toast.success(event.is_active ? '이벤트가 비활성화되었습니다.' : '이벤트가 활성화되었습니다.')
      
      setEvents(prev => prev.map(e => ({
        ...e,
        is_active: e.id === event.id ? !e.is_active : (event.is_active ? e.is_active : false),
      })))

    } catch (error) {
      console.error('[AdminReferral] 이벤트 토글 오류:', error)
      toast.error('이벤트 상태 변경 중 오류가 발생했습니다.')
    }
  }

  // 이벤트 삭제 (참조되고 있으면 삭제 불가)
  const handleDeleteEvent = async (eventId: number) => {
    if (!confirm('정말 삭제하시겠습니까?\n(참조하는 추천 데이터가 있으면 삭제할 수 없습니다)')) return

    try {
      const { error } = await supabase
        .from('referral_event')
        .delete()
        .eq('id', eventId)

      if (error) {
        if (error.code === '23503') {
          toast.error('이벤트를 참조하는 추천 데이터가 있어 삭제할 수 없습니다.')
        } else {
          throw error
        }
        return
      }

      toast.success('이벤트가 삭제되었습니다.')
      setEvents(prev => prev.filter(e => e.id !== eventId))

    } catch (error) {
      console.error('[AdminReferral] 이벤트 삭제 오류:', error)
      toast.error('이벤트 삭제 중 오류가 발생했습니다.')
    }
  }

  // 이벤트 상태 확인
  const getEventStatus = (event: ReferralEvent) => {
    const now = new Date()
    const start = new Date(event.start_at)
    const end = new Date(event.end_at)

    if (!event.is_active) return 'inactive'
    if (now < start) return 'scheduled'
    if (now > end) return 'ended'
    return 'active'
  }

  // 로딩 또는 권한 없음
  if (!user || !ADMIN_USER_IDS.includes(user.id)) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <p className="text-gray-500">접근 권한이 없습니다.</p>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-4xl">
      <Helmet>
        <title>어드민 - 초대장 관리</title>
      </Helmet>

      <h1 className="text-2xl font-bold mb-6">초대장 관리</h1>

      {/* 통계 카드 */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-gray-500" />
              <div>
                <p className="text-xs text-gray-500">전체 추천</p>
                <p className="text-xl font-bold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-500" />
              <div>
                <p className="text-xs text-gray-500">이번 달</p>
                <p className="text-xl font-bold text-blue-600">{stats.thisMonth}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500">결제 대기</p>
                <p className="text-xl font-bold text-gray-500">{stats.waitingPayment}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-yellow-500" />
              <div>
                <p className="text-xs text-gray-500">승인 대기</p>
                <p className="text-xl font-bold text-yellow-600">{stats.pending}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <div>
                <p className="text-xs text-gray-500">승인 완료</p>
                <p className="text-xl font-bold text-green-600">{stats.approved}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 결제 대기 목록 (가입했지만 아직 첫 결제 안 함) */}
      <Card className="mb-6">
        <CardHeader 
          className="cursor-pointer"
          onClick={() => setExpandedSections(prev => ({ ...prev, waitingPayment: !prev.waitingPayment }))}
        >
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-gray-400" />
              결제 대기 ({stats.waitingPayment})
            </span>
            {expandedSections.waitingPayment ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </CardTitle>
        </CardHeader>
        {expandedSections.waitingPayment && (
          <CardContent>
            {waitingPaymentReferrals.length === 0 ? (
              <p className="text-center text-gray-500 py-4">결제 대기 중인 추천이 없습니다.</p>
            ) : (
              <div className="space-y-3">
                {waitingPaymentReferrals.map((referral) => (
                  <div 
                    key={referral.id}
                    className="border rounded-lg p-4 bg-gray-50 border-gray-200"
                  >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                      <div className="space-y-1">
                        <p className="font-medium">
                          피추천인: <span className="text-brand-primary">{referral.referee_nickname}</span>
                          {' '}← 추천인: <span className="text-gray-600">{referral.referrer_nickname}</span>
                        </p>
                        <p className="text-sm text-gray-600 flex items-center gap-2">
                          <span className="px-2 py-0.5 bg-gray-200 rounded text-xs">
                            {CHANNEL_PLATFORM_LABELS[referral.channel_platform] || referral.channel_platform}
                          </span>
                          <a 
                            href={referral.channel_link.startsWith('http') ? referral.channel_link : `https://${referral.channel_link}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline flex items-center gap-1"
                          >
                            {referral.channel_link}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </p>
                        <p className="text-xs text-gray-500">
                          가입일: {formatDateTime(referral.created_at)}
                          {referral.is_event_period && <span className="ml-2 text-purple-500">🎉 이벤트 기간</span>}
                        </p>
                      </div>
                      <div className="flex items-center">
                        <span className="px-3 py-1 bg-gray-200 text-gray-600 rounded-full text-sm">
                          ⏳ 첫 결제 대기 중
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* 승인 대기 목록 */}
      <Card className="mb-6">
        <CardHeader 
          className="cursor-pointer"
          onClick={() => setExpandedSections(prev => ({ ...prev, pending: !prev.pending }))}
        >
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-yellow-500" />
              승인 대기 ({stats.pending})
            </span>
            {expandedSections.pending ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </CardTitle>
        </CardHeader>
        {expandedSections.pending && (
          <CardContent>
            {pendingReferrals.length === 0 ? (
              <p className="text-center text-gray-500 py-4">승인 대기 중인 추천이 없습니다.</p>
            ) : (
              <div className="space-y-3">
                {pendingReferrals.map((referral) => (
                  <div 
                    key={referral.id}
                    className="border rounded-lg p-4 bg-yellow-50 border-yellow-200"
                  >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                      <div className="space-y-1">
                        <p className="font-medium">
                          피추천인: <span className="text-brand-primary">{referral.referee_nickname}</span>
                          {' '}← 추천인: <span className="text-gray-600">{referral.referrer_nickname}</span>
                        </p>
                        <p className="text-sm text-gray-600 flex items-center gap-2">
                          <span className="px-2 py-0.5 bg-gray-200 rounded text-xs">
                            {CHANNEL_PLATFORM_LABELS[referral.channel_platform] || referral.channel_platform}
                          </span>
                          <a 
                            href={referral.channel_link.startsWith('http') ? referral.channel_link : `https://${referral.channel_link}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline flex items-center gap-1"
                          >
                            {referral.channel_link}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </p>
                        <p className="text-xs text-gray-500">
                          첫 결제: {referral.first_payment_at ? formatDateTime(referral.first_payment_at) : '-'}
                          {referral.is_event_period && <span className="ml-2 text-purple-500">🎉 이벤트 기간</span>}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="border-red-300 text-red-600 hover:bg-red-50"
                          onClick={() => setRejectingReferralId(referral.id)}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          거절
                        </Button>
                        <Button 
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => handleApprove(referral)}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          승인
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* 이벤트 관리 */}
      <Card>
        <CardHeader 
          className="cursor-pointer"
          onClick={() => setExpandedSections(prev => ({ ...prev, events: !prev.events }))}
        >
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-purple-500" />
              이벤트 관리
            </span>
            {expandedSections.events ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </CardTitle>
        </CardHeader>
        {expandedSections.events && (
          <CardContent>
            {/* 이벤트 생성 버튼 */}
            {!showEventForm && (
              <Button 
                variant="outline" 
                className="w-full mb-4"
                onClick={() => setShowEventForm(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                새 이벤트 등록
              </Button>
            )}

            {/* 이벤트 생성 폼 */}
            {showEventForm && (
              <div className="border rounded-lg p-4 mb-4 bg-gray-50">
                <h3 className="font-medium mb-3">새 이벤트 등록</h3>
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="eventName">이벤트 이름</Label>
                    <Input
                      id="eventName"
                      value={eventForm.name}
                      onChange={(e) => setEventForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="예: 신년 특별 이벤트"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="startAt">시작일시</Label>
                      <Input
                        id="startAt"
                        type="datetime-local"
                        value={eventForm.start_at}
                        onChange={(e) => setEventForm(prev => ({ ...prev, start_at: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label htmlFor="endAt">종료일시</Label>
                      <Input
                        id="endAt"
                        type="datetime-local"
                        value={eventForm.end_at}
                        onChange={(e) => setEventForm(prev => ({ ...prev, end_at: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setShowEventForm(false)}>
                      취소
                    </Button>
                    <Button onClick={handleCreateEvent}>
                      등록
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* 이벤트 목록 */}
            {events.length === 0 ? (
              <p className="text-center text-gray-500 py-4">등록된 이벤트가 없습니다.</p>
            ) : (
              <div className="space-y-3">
                {events.map((event) => {
                  const status = getEventStatus(event)
                  return (
                    <div 
                      key={event.id}
                      className={`border rounded-lg p-4 ${
                        status === 'active' ? 'bg-green-50 border-green-300' :
                        status === 'scheduled' ? 'bg-blue-50 border-blue-200' :
                        status === 'ended' ? 'bg-gray-50 border-gray-200' :
                        'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium">{event.name}</span>
                            {status === 'active' && (
                              <span className="px-2 py-0.5 bg-green-200 text-green-700 rounded-full text-xs">진행 중</span>
                            )}
                            {status === 'scheduled' && (
                              <span className="px-2 py-0.5 bg-blue-200 text-blue-700 rounded-full text-xs">예정</span>
                            )}
                            {status === 'ended' && (
                              <span className="px-2 py-0.5 bg-gray-200 text-gray-600 rounded-full text-xs">종료</span>
                            )}
                            {status === 'inactive' && (
                              <span className="px-2 py-0.5 bg-gray-200 text-gray-600 rounded-full text-xs">비활성</span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">
                            {formatDateTime(event.start_at)} ~ {formatDateTime(event.end_at)}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleToggleEvent(event)}
                            className={event.is_active ? 'border-red-300 text-red-600' : 'border-green-300 text-green-600'}
                          >
                            {event.is_active ? (
                              <>
                                <PowerOff className="w-4 h-4 mr-1" />
                                비활성화
                              </>
                            ) : (
                              <>
                                <Power className="w-4 h-4 mr-1" />
                                활성화
                              </>
                            )}
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="border-red-300 text-red-600 hover:bg-red-50"
                            onClick={() => handleDeleteEvent(event.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* 거절 사유 모달 */}
      {rejectingReferralId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-bold mb-4">거절 사유 입력</h3>
            <div className="space-y-4">
              <div>
                <Label htmlFor="rejectionReason">거절 사유</Label>
                <Input
                  id="rejectionReason"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="예: 채널 정보 확인 불가"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setRejectingReferralId(null)
                    setRejectionReason('')
                  }}
                >
                  취소
                </Button>
                <Button 
                  className="bg-red-600 hover:bg-red-700"
                  onClick={handleReject}
                >
                  거절
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
